import { NgClass, CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, Inject, PLATFORM_ID, ViewChild, ElementRef, AfterViewInit, computed, inject, NgZone, HostListener } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink, RouterOutlet, NavigationEnd, NavigationStart } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { defaultNavigation } from 'app/core/navigation/navigation.data';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Subject, takeUntil, filter, lastValueFrom } from 'rxjs';
import { LandingFooterComponent } from "app/modules/landing/footer/footer.component";
import { FuseNavigationItem } from '@fuse/components/navigation';
import { AuthService } from 'app/core/auth/auth.service';
import { MatMenuModule } from '@angular/material/menu';
import { RouteTrackingService } from 'app/core/services/route-tracking.service';
import { PublicContentSnapshotService } from 'app/core/services/public-content-snapshot.service';
import { TransferStateService } from 'app/core/services/transfer-state.service';
import { StrapiService, SiteHeaderSettings } from 'app/modules/landing/dealers/strapi.service';
import { VehiclesService } from 'app/modules/landing/vehicles/vehicles.service';
import { GtagService } from 'app/core/services/gtag.service';
import { MetaPixelService } from 'app/core/services/meta-pixel.service';
import { slugify } from 'app/core/utils/slugify';
import { environment } from 'environments/environment';

interface SidebarItem {
    label: string;
    icon: string;
    route: string;
    page: string;
    type?: 'basic' | 'collapsable';
    children?: SidebarItem[];
    isExpanded?: boolean;
}

interface PrimaryNavLink {
    id: string;
    label: string;
    route?: string;
    type?: 'basic' | 'collapsable';
    children?: Array<{ label: string; route: string }>;
}

export interface VehicleModel {
    id: string;
    name: string;
    subtitle: string;
    description: string;
    price: string;
    image?: {
        url: string;
    };
    imageUrl?: string;
    tag: string;
    featured?: boolean;
    available?: boolean;
    hasSubVehicles?: boolean;
    subVehicles?: VehicleModel[];
}

interface HeritageFact {
    title: string;
    image: {
        url: string;
    };
    hyperlink?: string;
}

@Component({
    selector: 'modern-layout',
    templateUrl: './modern.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        MatButtonModule,
        MatIconModule,
        RouterLink,
        RouterOutlet,
        NgClass,
        LandingFooterComponent,
        CommonModule,
        MatMenuModule,
        NgOptimizedImage
    ],
})
export class ModernLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('bgVideo') bgVideo!: ElementRef<HTMLVideoElement>;

    private _transferState = inject(TransferStateService);
    private _publicContentSnapshotService = inject(PublicContentSnapshotService);

    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    isHeaderScrolled = false;
    isHomePageRoute: boolean = false;
    showHeaderLogo: boolean = false;
    isInventoryRoute: boolean = false;
    isBrowser: boolean = false;

    // Sidebar properties
    activePage: string = 'dashboard';
    token: boolean;
    sidebarOpen = false;
    showLabels = true;
    memberUrl: boolean;
    isMobile = false; // Will be set in ngOnInit with platform check

    sidebarItems: SidebarItem[] = [];
    dashboardItems: SidebarItem[] = [];

    readonly secondaryNavLinks = [
        { label: 'DEALERS', route: '/dealers' },
        { label: 'FAQ', route: '/faq' },
        { label: 'CONTACT', route: '/contact' },
    ];

    /**
     * Analytics event names for top-level navigation, keyed by route.
     *
     * Keyed by route rather than by label so the desktop utility bar and the
     * mobile sidebar report the same event for the same destination — tracking
     * only the desktop links would silently under-count every phone visitor.
     * Add a route here to start tracking it; unlisted routes report nothing.
     */
    private static readonly NAV_EVENTS: Record<string, { ga: string; meta: string }> = {
        '/dealers': { ga: 'nav_dealers_click', meta: 'NavDealersClick' },
        '/faq': { ga: 'nav_faq_click', meta: 'NavFaqClick' },
        '/contact': { ga: 'nav_contact_click', meta: 'NavContactClick' },
    };

    readonly primaryNavLinks: PrimaryNavLink[] = [
        { id: 'inventory', label: 'INVENTORY', route: '/inventory' },
        { id: 'vehicles', label: 'MODELS', route: '/vehicles' },
        { id: 'license', label: 'LICENCING', route: '/license' },
        { id: 'news', label: 'NEWS', route: '/news' },
        { id: 'events', label: 'EVENTS', route: '/events' },
        {
            id: 'about-group',
            label: 'ABOUT US',
            type: 'collapsable',
            children: [
                { label: 'Company', route: '/about' },
                { label: 'Factory', route: '/factory' },
                { label: 'Gallery', route: '/gallery' },
                { label: 'Testimonials', route: '/testimonials' },
            ],
        },
    ];

    siteHeaderSlogan = 'ICONIC PERFORMANCE';
    dealerPhone = '(800) 297-6253';
    dealerPhoneHref = 'tel:+18002976253';

    // Landing component properties
    vehicleModels = computed(() => this.vehiclesService?.vehicleModels() || []);
    isLoadingLanding = computed(() => this.vehiclesService?.isLoading() || false);

    // Video properties
    isLocal: boolean = false;
    homepageVideoUrl: string = '';
    videoSrc: string = '';
    videoElementSrc: string | null = null;
    isVideoReady = false;
    isVideoPaused = false;
    isVideoMuted = true;
    showHeroContent = true; // Always show content by default
    isVideoSectionVisible = true;
    _videoWasPreviouslyLoaded = false; // Track if video was ever successfully loaded
    licenseSection: any = null;
    homeVideoMain: any = null;
    private _videoEventListeners: Array<{ event: string; handler: EventListener }> = [];
    private _fadeOutTimer: any = null;
    private _contentFadeOutDelay = 7000; // 7 seconds

    // Heritage section data
    heritageData: any = null;
    heritageFacts: HeritageFact[] = [];
    selectedVideoUrl: SafeResourceUrl | null = null;

    // Footer data
    vehicles: any[] = [];
    slugify = slugify;
    isLoadingVehicles = true;
    countryFlagImage: any = null;
    private _scrollRafId: number | null = null;
    private _videoVisibilityObserver: IntersectionObserver | null = null;
    private readonly _scrollListener = () => this.scheduleScrollStateUpdate();
    private _heroVideoStartTimer: number | null = null;
    private _heroVideoIdleCallbackId: number | null = null;
    private _heroVideoLoadHandler: (() => void) | null = null;


    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        public _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private cdr: ChangeDetectorRef,
        private _ngZone: NgZone,
        @Inject(PLATFORM_ID) private platformId: any,
        public authService: AuthService,
        private routeTrackingService: RouteTrackingService,
        public strapiService: StrapiService,
        private vehiclesService: VehiclesService,
        private _domSanitizer: DomSanitizer,
        private _gtagService: GtagService,
        private _metaPixelService: MetaPixelService
    ) {
        this.initializeSidebarItems();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Track click on footer dealer hotline (+1800 29 SNAKE)
     */
    onDealerHotlineClick(): void {
        this._gtagService.event('dealer_hotline_click', { location: 'footer', label: '+1800 29 SNAKE (Dealer Hotline)' });
        this._metaPixelService.trackCustom('DealerHotlineClick', { location: 'footer', label: '+1800 29 SNAKE (Dealer Hotline)' });
    }

    onHeaderPhoneClick(): void {
        this._gtagService.event('dealer_hotline_click', {
            location: 'header',
            phone: this.dealerPhone,
        });
        this._metaPixelService.trackCustom('DealerHotlineClick', {
            location: 'header',
            phone: this.dealerPhone,
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
                this.updateSidebarItems();
                this.cdr.markForCheck();
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('lg');
            });

        // Set browser flag for template
        this.isBrowser = isPlatformBrowser(this.platformId);

        // Setup user interaction detection for video autoplay with sound
        if (this.isBrowser) {
            this.setupInteractionDetection();
        }

        // Sidebar initialization
        this.token = !!this.authService.accessToken;

        // Check mobile status only in browser
        if (this.isBrowser) {
            this.isMobile = window.innerWidth < 1024;
        }

        if (this.isMobile) {
            this.sidebarOpen = false;
            this.showLabels = true;
        }

        const currentUrl = this._router.url;
        this.memberUrl = currentUrl.includes('/dashboard');
        this.isHomePageRoute = currentUrl === '/home';
        this.isInventoryRoute = currentUrl.includes('/inventory');
        this.updateSidebarItems();
        this.updateActiveFromUrl(currentUrl);
        this.loadSiteHeader();

        // Hydrate landing data synchronously from the resolver so the video
        // URL is baked into SSR HTML and the browser starts the fetch during
        // HTML parse (before Angular hydrates).
        if (this.isLandingPage()) {
            const pageData = this._activatedRoute.firstChild?.snapshot.data?.['pageData'];
            if (pageData) {
                this.applyResolvedLandingPageData(pageData);
                // Set videoElementSrc HERE (initial load / SSR hydration only).
                // applyResolvedLandingPageData is also called on SPA navigation —
                // in that case we must NOT set videoElementSrc eagerly because
                // the autoplay attribute + unlocked audio would play sound,
                // which initializeVideo() then kills by setting muted=true.
                if (this.videoSrc) {
                    this.videoElementSrc = this.videoSrc;
                }
            }
        }

        // Listen to route tracking service for home page changes
        this.routeTrackingService.isHomePage$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isHomePage: boolean) => {
                this.isHomePageRoute = isHomePage;
                // Route tracking service updated home page status
                this.cdr.detectChanges();
            });

        // Listen to header content visibility changes
        this.routeTrackingService.showHeaderContent$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((showContent: boolean) => {
                this.showHeaderLogo = showContent;
                this.cdr.detectChanges();
            });

        // Pause hero video immediately when navigation starts to prevent audio bleeding
        // into other pages before the @if removes the <video> element from the DOM.
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationStart),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationStart) => {
                const leavingLanding = this.isLandingPage() && !(event.url === '/' || event.url === '/landing' || event.url.startsWith('/landing'));
                if (leavingLanding) {
                    this.cancelHeroVideoStart();
                    if (this.bgVideo?.nativeElement) {
                        const video = this.bgVideo.nativeElement;
                        video.pause();
                        video.muted = true;
                        video.removeAttribute('src');
                        video.load();
                    }
                    this.videoElementSrc = null;
                    this.isVideoMuted = true;
                    this.isVideoPaused = true;
                    this.isVideoReady = false;
                    this.cdr.detectChanges();
                }
            });

        // Keep original router events for other navigation
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                this.memberUrl = url.includes('/home');
                this.isInventoryRoute = url.includes('/inventory');
                this.updateSidebarItems();
                this.updateActiveFromUrl(url);

                // Load landing data when navigating to landing page
                if (this.isLandingPage()) {
                    requestAnimationFrame(() => this.setupVideoVisibilityObserver());

                    this.loadLandingData().then(() => {
                        if (this.bgVideo?.nativeElement && this.videoSrc) {
                            this.scheduleHeroVideoStart();
                        } else {
                            requestAnimationFrame(() => {
                                if (this.bgVideo?.nativeElement && this.videoSrc) {
                                    this.scheduleHeroVideoStart();
                                }
                            });
                        }
                    });
                } else {
                    this.disconnectVideoVisibilityObserver();
                    this.resetVideoState();
                    this.isVideoSectionVisible = true;
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Clean up body scroll lock
        if (isPlatformBrowser(this.platformId)) {
            window.removeEventListener('scroll', this._scrollListener);

            if (this._scrollRafId !== null) {
                window.cancelAnimationFrame(this._scrollRafId);
                this._scrollRafId = null;
            }

            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }

        // Clean up video: pause, mute, and remove event listeners
        if (isPlatformBrowser(this.platformId) && this.bgVideo?.nativeElement) {
            const video = this.bgVideo.nativeElement;
            video.pause();
            video.muted = true;
            video.removeAttribute('src');
            video.load();
            this._videoEventListeners.forEach(({ event, handler }) => {
                video.removeEventListener(event, handler);
            });
            this._videoEventListeners = [];
        }

        // Clear fade out timer
        if (this._fadeOutTimer) {
            clearTimeout(this._fadeOutTimer);
            this._fadeOutTimer = null;
        }

        this.cancelHeroVideoStart();
        this.videoElementSrc = null;
        this.disconnectVideoVisibilityObserver();
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        // Prevent horizontal overflow on mobile
        if (isPlatformBrowser(this.platformId)) {
            document.body.style.overflowX = 'hidden';
            document.documentElement.style.overflowX = 'hidden';
            this.setupScrollEffects();
        }

        if (this.isLandingPage()) {
            this.setupVideoVisibilityObserver();
            this.loadLandingData();
        }
    }

    private setupScrollEffects(): void {
        if (!this.isBrowser) {
            return;
        }

        this._ngZone.runOutsideAngular(() => {
            window.removeEventListener('scroll', this._scrollListener);
            window.addEventListener('scroll', this._scrollListener, { passive: true });
        });

        this.updateScrollState();
    }

    private scheduleScrollStateUpdate(): void {
        if (this._scrollRafId !== null) {
            return;
        }

        this._scrollRafId = window.requestAnimationFrame(() => {
            this._scrollRafId = null;
            this.updateScrollState();
        });
    }

    private updateScrollState(): void {
        const nextHeaderScrolled = window.scrollY > 30;

        if (nextHeaderScrolled === this.isHeaderScrolled) {
            return;
        }

        this._ngZone.run(() => {
            this.isHeaderScrolled = nextHeaderScrolled;
            this.cdr.markForCheck();
        });
    }

    private setupVideoVisibilityObserver(): void {
        if (!this.isBrowser || !this.isLandingPage()) {
            return;
        }

        if (typeof window.IntersectionObserver === 'undefined') {
            return;
        }

        const videoSection = document.getElementById('video-hero-section');
        if (!videoSection) {
            return;
        }

        this.disconnectVideoVisibilityObserver();

        this._ngZone.runOutsideAngular(() => {
            this._videoVisibilityObserver = new IntersectionObserver(
                ([entry]) => {
                    const nextVisibility = entry?.isIntersecting ?? true;

                    if (nextVisibility === this.isVideoSectionVisible) {
                        return;
                    }

                    this._ngZone.run(() => {
                        this.isVideoSectionVisible = nextVisibility;

                        if (nextVisibility && this.videoSrc && !this.videoElementSrc) {
                            this.scheduleHeroVideoStart();
                        } else if (!nextVisibility && !this._videoWasPreviouslyLoaded) {
                            this.cancelHeroVideoStart();
                        }

                        this.cdr.markForCheck();
                    });
                },
                {
                    threshold: 0.01,
                    rootMargin: '-100px 0px -100px 0px',
                }
            );

            this._videoVisibilityObserver.observe(videoSection);
        });
    }

    private disconnectVideoVisibilityObserver(): void {
        this._videoVisibilityObserver?.disconnect();
        this._videoVisibilityObserver = null;
    }

    private scheduleHeroVideoStart(): void {
        if (
            !isPlatformBrowser(this.platformId) ||
            !this.videoSrc ||
            !this.bgVideo?.nativeElement ||
            !this.isLandingPage()
        ) {
            return;
        }

        this.cancelHeroVideoStart();

        if (this.shouldSkipHeroVideoAutoplay()) {
            return;
        }

        const start = () => {
            this._heroVideoStartTimer = null;

            if (!this.isLandingPage() || !this.isVideoSectionVisible) {
                return;
            }

            this.startHeroVideo();
        };

        const scheduleWithDelay = () => {
            const delay = this.getHeroVideoStartDelay();
            this._heroVideoStartTimer = window.setTimeout(start, delay);
        };

        if (document.readyState !== 'complete') {
            this._heroVideoLoadHandler = () => {
                this._heroVideoLoadHandler = null;
                scheduleWithDelay();
            };
            window.addEventListener('load', this._heroVideoLoadHandler, { once: true });
            return;
        }

        const requestIdle = (window as any).requestIdleCallback as
            | ((callback: () => void, options?: { timeout: number }) => number)
            | undefined;

        if (typeof requestIdle === 'function') {
            this._heroVideoIdleCallbackId = requestIdle(
                () => {
                    this._heroVideoIdleCallbackId = null;
                    scheduleWithDelay();
                },
                { timeout: 2000 }
            );
            return;
        }

        scheduleWithDelay();
    }

    private cancelHeroVideoStart(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if (this._heroVideoStartTimer !== null) {
            window.clearTimeout(this._heroVideoStartTimer);
            this._heroVideoStartTimer = null;
        }

        if (this._heroVideoIdleCallbackId !== null) {
            const cancelIdle = (window as any).cancelIdleCallback as
                | ((handle: number) => void)
                | undefined;

            if (typeof cancelIdle === 'function') {
                cancelIdle(this._heroVideoIdleCallbackId);
            }

            this._heroVideoIdleCallbackId = null;
        }

        if (this._heroVideoLoadHandler) {
            window.removeEventListener('load', this._heroVideoLoadHandler);
            this._heroVideoLoadHandler = null;
        }
    }

    private shouldSkipHeroVideoAutoplay(): boolean {
        if (!isPlatformBrowser(this.platformId)) {
            return true;
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return true;
        }

        const connection = (navigator as any)?.connection;
        const effectiveType = String(connection?.effectiveType || '').toLowerCase();

        return !!connection?.saveData || effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g';
    }

    private getHeroVideoStartDelay(): number {
        const connection = (navigator as any)?.connection;
        const effectiveType = String(connection?.effectiveType || '').toLowerCase();
        const hasInteracted = this.hasUserInteracted();

        if (effectiveType === '4g' && hasInteracted) {
            return this.isScreenSmall ? 1200 : 800;
        }

        if (effectiveType === '4g') {
            return this.isScreenSmall ? 2200 : 1600;
        }

        return this.isScreenSmall ? 3200 : 2400;
    }

    /**
     * Check if current route is landing page
     */
    isLandingPage(): boolean {
        const url = this._router.url;
        return url === '/' || url === '/landing' || url.startsWith('/landing');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Sidebar methods
    // -----------------------------------------------------------------------------------------------------

    private initializeSidebarItems(): void {
        this.updateSidebarItems();
    }

    private updateSidebarItems(): void {
        const sidebarNavigation =
            this.navigation?.default?.length > 0
                ? this.navigation.default
                : defaultNavigation;

        this.sidebarItems = this.convertNavigationToSidebarItems(sidebarNavigation);
    }

    private convertNavigationToSidebarItems(navigationItems: FuseNavigationItem[]): SidebarItem[] {
        return navigationItems.map(item => ({
            label: item.title || '',
            icon: item.icon || '',
            route: item.link || '',
            page: item.id || '',
            type: item.type as 'basic' | 'collapsable',
            children: item.children ? this.convertNavigationToSidebarItems(item.children) : undefined,
            isExpanded: false
        }));
    }

    private convertIconName(fuseIcon: string): string {
        const iconMap: { [key: string]: string } = {
            'heroicons_outline:home': 'home',
            'heroicons_outline:information-circle': 'info',
            'heroicons_outline:photo': 'photo_library',
            'heroicons_outline:briefcase': 'business_center',
            'heroicons_outline:question-mark-circle': 'help',
            'heroicons_outline:envelope': 'mail',
            'heroicons_outline:building-office': 'factory'
        };

        return iconMap[fuseIcon] || 'circle';
    }

    public redirectToWhatsApp() {
        if (isPlatformBrowser(this.platformId)) {
            window.open('https://api.whatsapp.com/send/?phone=0600687377&text=Please+can+you+assist+me', '_blank');
        }
    }

    logout() {
        this.authService.signOut();
        this._router.navigateByUrl("/");
    }

    setActive(page: string): void {
        // if (this.isMobile && page !== this.activePage && this.sidebarOpen) {
        this.sidebarOpen = false;
        // }
        this.activePage = page;

        // Reset body scroll lock when closing sidebar
        if (isPlatformBrowser(this.platformId)) {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
    }

    toggleSidebar() {
        this.sidebarOpen = !this.sidebarOpen;

        // Prevent body scroll when sidebar is open on mobile
        if (isPlatformBrowser(this.platformId)) {
            if (this.sidebarOpen && this.isMobile) {
                document.body.style.overflow = 'hidden';
                document.body.style.position = 'fixed';
                document.body.style.width = '100%';
            } else {
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
            }
        }

        if (!this.isMobile) {
            if (!this.sidebarOpen) {
                this.showLabels = false;
            } else {
                setTimeout(() => {
                    this.showLabels = true;
                }, 200);
            }
        } else {
            this.showLabels = true;
        }
    }

    goHome() {
        this._router.navigateByUrl("/");
    }

    private updateActiveFromUrl(url: string): void {
        if (!url) {
            return;
        }

        // Check for matches including children of collapsible items
        let match: SidebarItem | undefined;

        for (const item of this.sidebarItems) {
            // Check direct match
            if (item.route && url.startsWith(item.route)) {
                match = item;
                continue;
            }

            // Check children matches for collapsible items
            if (item.type === 'collapsable' && item.children) {
                const childMatch = item.children.find(child =>
                    child.route && url.startsWith(child.route)
                );
                if (childMatch) {
                    match = childMatch;
                    // Expand parent if child is active
                    item.isExpanded = true;
                    break;
                }
            }
        }

        if (match) {
            this.activePage = match.page;
        }
    }

    // @HostListener('window:resize', ['$event'])
    // onResize(event: any) {
    //     const newIsMobile = event.target.innerWidth < 1024;

    //     if (newIsMobile !== this.isMobile) {
    //         this.isMobile = newIsMobile;

    //         if (this.isMobile) {
    //             this.sidebarOpen = false;
    //             this.showLabels = true;
    //         } else {
    //             this.sidebarOpen = true;
    //             this.showLabels = true;
    //         }
    //     }
    // }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.isMobile || !this.sidebarOpen) {
            return;
        }

        const sidebarEl = document.querySelector('.sidebar-container');
        const hamburgerBtn = document.querySelector('.fixed.top-4.left-4');

        if (sidebarEl && !sidebarEl.contains(event.target as Node) &&
            hamburgerBtn && !hamburgerBtn.contains(event.target as Node)) {
            this.sidebarOpen = false;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle collapsible menu item
     * @param item The sidebar item to toggle
     */
    toggleCollapsibleItem(item: SidebarItem): void {
        if (item.type === 'collapsable' && item.children) {
            item.isExpanded = !item.isExpanded;
        }
    }

    /**
     * Navigate to child item
     * @param childItem The child item to navigate to
     * @param parentItem The parent item
     */
    navigateToChild(childItem: SidebarItem, parentItem: SidebarItem): void {
        this.setActive(childItem.page);
        this.sidebarOpen = false;
        this._router.navigate([childItem.route]);
    }

    /**
     * Check if current route matches collapsible item children
     * @param item The collapsible item to check
     */
    isCollapsibleItemActive(item: SidebarItem): boolean {
        if (item.type !== 'collapsable' || !item.children) {
            return false;
        }
        const currentUrl = this._router.url;
        return item.children.some(child => currentUrl.startsWith(child.route));
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // For modern layout, toggle the custom sidebar instead
        this.toggleSidebar();
    }

    /**
     * Check if current page is exactly the home page
     */
    isHomePage(): boolean {
        // Check if current route is home page
        return this.isHomePageRoute;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Landing component methods
    // -----------------------------------------------------------------------------------------------------

    async loadVehicleModels(): Promise<void> {
        await this.vehiclesService.loadVehicleModels();
    }

    async loadHeritageData(): Promise<void> {
        try {
            // Use Transfer State to prevent duplicate API calls on SSR hydration
            const response: any = await lastValueFrom(
                this._transferState.useTransferState(
                    'heritage-page',
                    () => this.strapiService.getHeritagePage()
                )
            );

            if (response && response.data) {
                this.heritageData = response.data;
                this.heritageFacts = response.data.facts || [];
            }
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading heritage data:', error);
        }
    }

    initializeVideo(): void {
        if (!this.bgVideo?.nativeElement || !isPlatformBrowser(this.platformId)) {
            return;
        }

        if (!this.videoElementSrc) {
            return;
        }

        const video = this.bgVideo.nativeElement;

        // Clean up previous event listeners and timers
        this._videoEventListeners.forEach(({ event, handler }) => {
            video.removeEventListener(event, handler);
        });
        this._videoEventListeners = [];

        if (this._fadeOutTimer) {
            clearTimeout(this._fadeOutTimer);
            this._fadeOutTimer = null;
        }

        // Ensure content is visible initially
        this.showHeroContent = true;

        const loadedmetadataHandler = () => {
            this.isVideoReady = true;
            this._videoWasPreviouslyLoaded = true;
            this.cdr.detectChanges();
        };

        const canplayHandler = () => {
            this.isVideoReady = true;
            this._videoWasPreviouslyLoaded = true;
            this.cdr.detectChanges();
        };

        const playingHandler = () => {
            // Start fade-out timer after video begins playing
            if (this._fadeOutTimer) {
                clearTimeout(this._fadeOutTimer);
            }
            this._fadeOutTimer = setTimeout(() => {
                this.showHeroContent = false;
                this.cdr.detectChanges();
            }, this._contentFadeOutDelay);
        };

        const timeupdateHandler = () => {
            // Fade content back in during the last 10 seconds of the video
            if (video.duration && video.currentTime) {
                const timeRemaining = video.duration - video.currentTime;
                if (timeRemaining <= 10 && !this.showHeroContent) {
                    this.showHeroContent = true;
                    this.cdr.detectChanges();
                }
                // If video looped back to beginning, restart the fade cycle
                if (video.currentTime < 1 && !this.showHeroContent) {
                    this.showHeroContent = true;
                    this.cdr.detectChanges();
                    // Restart fade-out timer
                    if (this._fadeOutTimer) {
                        clearTimeout(this._fadeOutTimer);
                    }
                    this._fadeOutTimer = setTimeout(() => {
                        this.showHeroContent = false;
                        this.cdr.detectChanges();
                    }, this._contentFadeOutDelay);
                }
            }
        };

        const loadeddataHandler = () => {
            // Always start muted to ensure autoplay works
            video.muted = true;
            this.isVideoMuted = true;

            // Try to play the video with better error handling
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        this.isVideoPaused = false;
                        this.cdr.detectChanges();

                        if (!this.hasUserInteracted()) {
                            return;
                        }

                        // Only try sound after the user has already interacted.
                        setTimeout(() => {
                            video.muted = false;
                            this.isVideoMuted = false;
                            this.cdr.detectChanges();

                            setTimeout(() => {
                                if (video.paused) {
                                    video.muted = true;
                                    this.isVideoMuted = true;
                                    video.play().catch(e => {
                                        console.error('Failed to resume muted video:', e);
                                    });
                                    this.cdr.detectChanges();
                                }
                            }, 100);
                        }, 500);
                    })
                    .catch(err => {
                        // Ensure it's muted and try again
                        video.muted = true;
                        this.isVideoMuted = true;
                        video.play().catch(e => {
                            console.error('Video autoplay failed even when muted:', e);
                        });
                    });
            }
        };

        const errorHandler = (e: any) => {
            console.error('Video error:', e);
            // Even if video fails, content should remain visible
            this.isVideoReady = false;
            this.cdr.detectChanges();
        };

        video.addEventListener('loadedmetadata', loadedmetadataHandler);
        video.addEventListener('canplay', canplayHandler);
        video.addEventListener('playing', playingHandler);
        video.addEventListener('timeupdate', timeupdateHandler);
        video.addEventListener('loadeddata', loadeddataHandler);
        video.addEventListener('error', errorHandler);

        this._videoEventListeners.push(
            { event: 'loadedmetadata', handler: loadedmetadataHandler },
            { event: 'canplay', handler: canplayHandler },
            { event: 'playing', handler: playingHandler },
            { event: 'timeupdate', handler: timeupdateHandler },
            { event: 'loadeddata', handler: loadeddataHandler },
            { event: 'error', handler: errorHandler }
        );

        // Set video attributes to ensure best chance of autoplay
        // Always start muted - we'll try to unmute after video starts playing
        video.muted = true;
        this.isVideoMuted = true;

        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        // Try to load the video
        if (video.readyState >= video.HAVE_CURRENT_DATA) {
            // Video already has data (browser cache hit) — play instantly
            this.isVideoReady = true;
            this._videoWasPreviouslyLoaded = true;
            video.play().catch(err => {
            });
            this.cdr.detectChanges();
        } else if (this._videoWasPreviouslyLoaded) {
            // Video was loaded before but element is fresh — keep isVideoReady true and load
            this.isVideoReady = true;
            video.load();
            this.cdr.detectChanges();
        } else {
            video.load();
        }
    }

    private startHeroVideo(): void {
        if (!this.videoSrc || !this.bgVideo?.nativeElement || !isPlatformBrowser(this.platformId)) {
            return;
        }

        if (this.videoElementSrc !== this.videoSrc) {
            this.videoElementSrc = this.videoSrc;
            this.isVideoReady = false;
            this.showHeroContent = true;
            this.cdr.detectChanges();
        }

        requestAnimationFrame(() => this.initializeVideo());
    }

    private resetVideoState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        this.cancelHeroVideoStart();

        if (this._fadeOutTimer) {
            clearTimeout(this._fadeOutTimer);
            this._fadeOutTimer = null;
        }

        this.isVideoReady = false;
        this.isVideoPaused = false;
        this.isVideoMuted = true;
        this.showHeroContent = true;
        this.videoElementSrc = null;

        if (this.bgVideo?.nativeElement) {
            const video = this.bgVideo.nativeElement;
            video.pause();
            video.removeAttribute('src');
            video.load();
        }

        this.cdr.markForCheck();
    }

    /**
     * Setup user interaction detection
     * Listens for user interactions and stores in localStorage
     */
    private setupInteractionDetection(): void {
        const interactionEvents = ['click', 'scroll', 'keydown', 'touchstart', 'mousemove'];

        const handleInteraction = () => {
            localStorage.setItem('userHasInteracted', 'true');
            // Remove listeners after first interaction
            interactionEvents.forEach(event => {
                document.removeEventListener(event, handleInteraction);
            });
        };

        interactionEvents.forEach(event => {
            document.addEventListener(event, handleInteraction, { passive: true });
        });
    }

    /**
     * Check if user has interacted with the site before
     */
    private hasUserInteracted(): boolean {
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }
        return localStorage.getItem('userHasInteracted') === 'true';
    }

    async loadHomepageVideo(): Promise<void> {
        try {
            // Use Transfer State to prevent duplicate API calls on SSR hydration
            const response: any = await lastValueFrom(
                this._transferState.useTransferState(
                    'home-video',
                    () => this.strapiService.getHomeVideo()
                )
            );


            if (response && response.data) {
                const videoData = response.data;


                // Use relative path or local video path
                if (this.isLocal && videoData.localVideoPath) {
                    this.videoSrc = videoData.localVideoPath;
                } else if (videoData.video?.url) {
                    this.videoSrc = this.normalizeToMediaProxy(
                        this.strapiService.getFullImageUrl(videoData.video.url) || ''
                    );
                } else {
                    console.warn('No video file URL found in response');
                }

                this.homepageVideoUrl = this.videoSrc;
                this.cdr.detectChanges();
            } else {
                console.warn('No video data returned from API');
            }
        } catch (error) {
            console.error('Error loading homepage video:', error);
        }
    }

    async loadLicenseSection(): Promise<void> {
        try {
            // Use Transfer State to prevent duplicate API calls on SSR hydration
            const response: any = await lastValueFrom(
                this._transferState.useTransferState(
                    'home-license-section',
                    () => this.strapiService.getHomeVideoLicenseSection()
                )
            );

            if (response && response.data) {
                const data = response.data;
                // Use pre-computed fullUrl from API if available, otherwise compute it
                this.licenseSection = {
                    ...data,
                    images: data.images?.map((img: any) => ({
                        ...img,
                        fullUrl: img.fullUrl || this.strapiService.getFullImageUrl(img.url)
                    })) || []
                };
            }
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading license section:', error);
        }
    }

    async loadHomeVideoMain(): Promise<void> {
        try {
            // Use Transfer State to prevent duplicate API calls on SSR hydration
            const response: any = await lastValueFrom(
                this._transferState.useTransferState(
                    'home-video-main',
                    () => this.strapiService.getHomeVideoMain()
                )
            );

            if (response && response.data) {
                const data = response.data;
                // Use pre-computed URLs from API if available, otherwise compute them
                this.homeVideoMain = {
                    ...data,
                    titleImageUrl: data.titleImageUrl || this.strapiService.getFullImageUrl(data.titleImage?.url),
                    countryFlagImageUrl: data.countryFlagImageUrl || this.strapiService.getFullImageUrl(data.countryFlagImage?.url),
                    galleryImages: data.galleryImages?.map((img: any) => ({
                        ...img,
                        fullUrl: img.fullUrl || this.strapiService.getFullImageUrl(img.url)
                    })) || []
                };
            }
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading home video main content:', error);
        }
    }

    toggleVideoPlayback(): void {
        if (!this.bgVideo?.nativeElement) {
            return;
        }

        if (!this.videoElementSrc && this.videoSrc) {
            this.startHeroVideo();
            this.isVideoPaused = false;
            this.cdr.detectChanges();
            return;
        }

        const video = this.bgVideo.nativeElement;
        if (video.paused) {
            video.play();
            this.isVideoPaused = false;
        } else {
            video.pause();
            this.isVideoPaused = true;
            // Clear fade timer when pausing
            if (this._fadeOutTimer) {
                clearTimeout(this._fadeOutTimer);
                this._fadeOutTimer = null;
            }
            // Show content when paused
            this.showHeroContent = true;
            this.cdr.detectChanges();
        }
    }

    toggleVideoMute(): void {
        if (!this.bgVideo?.nativeElement) {
            return;
        }

        const video = this.bgVideo.nativeElement;
        video.muted = !video.muted;
        this.isVideoMuted = video.muted;
    }

    navigateToVehicles(): void {
        this._router.navigate(['/vehicles']);
    }

    navigateToDealers(): void {
        this._router.navigate(['/dealers']);
    }

    /**
     * Track a top-level navigation click from either the desktop utility bar or
     * the mobile sidebar. Navigation itself is left to routerLink / the caller.
     */
    onNavLinkClick(route: string, location: 'header_secondary' | 'mobile_sidebar'): void {
        const event = ModernLayoutComponent.NAV_EVENTS[route];
        if (!event) {
            return;
        }
        this._gtagService.event(event.ga, { location, route });
        this._metaPixelService.trackCustom(event.meta, { location, route });
    }

    navigateToInventory(): void {
        this._gtagService.event('view_inventory_click', { location: 'header' });
        this._metaPixelService.trackCustom('ViewInventoryClick', { location: 'header' });
        this._router.navigate(['/inventory']);
    }

    async loadSiteHeader(): Promise<void> {
        try {
            const useLiveContent = (environment as { enableLivePublicContent?: boolean }).enableLivePublicContent;

            if (useLiveContent) {
                const settings = await lastValueFrom(
                    this._transferState.useTransferState(
                        'site-header-settings',
                        () => this.strapiService.getSiteHeaderSettings()
                    )
                );
                this.applySiteHeaderSettings(settings);
            } else {
                const snapshot = await this._publicContentSnapshotService.getPage<SiteHeaderSettings>('site-header');
                if (snapshot?.slogan) {
                    this.applySiteHeaderSettings(snapshot);
                }
            }

            this.cdr.markForCheck();
        } catch (error) {
            console.error('Error loading site header settings:', error);
        }
    }

    private applySiteHeaderSettings(settings: Partial<SiteHeaderSettings>): void {
        if (settings.slogan) {
            this.siteHeaderSlogan = settings.slogan;
        }
        if (settings.dealerPhone) {
            this.dealerPhone = settings.dealerPhone;
        }
        if (settings.dealerPhoneHref) {
            this.dealerPhoneHref = settings.dealerPhoneHref;
        }
    }

    isNavLinkActive(route: string): boolean {
        const url = this._router.url.split('?')[0];
        return url === route || url.startsWith(`${route}/`);
    }

    isAboutUsActive(): boolean {
        return this.aboutUsChildren.some((child) => this.isNavLinkActive(child.route));
    }

    get aboutUsChildren(): Array<{ label: string; route: string }> {
        return this.primaryNavLinks.find((link) => link.id === 'about-group')?.children ?? [];
    }

    exploreVehicle(model: VehicleModel): void {
        if (model.hasSubVehicles) {
            this._router.navigate(['/vehicles', slugify(model.name)], { state: { id: model.id } });
        } else {
            this._router.navigate(['/vehicles', slugify(model.name), 'detail'], { state: { id: model.id } });
        }
    }

    async loadLandingData(): Promise<void> {

        // Check if we're on localhost
        if (isPlatformBrowser(this.platformId) && window.location.hostname.includes('localhost')) {
            this.isLocal = true;
        }

        const pageData = this._activatedRoute.firstChild?.snapshot.data?.['pageData'];
        if (pageData) {
            this.applyResolvedLandingPageData(pageData);
            return;
        }

        // Try to get cached video URL first (synchronous, instant)
        if (this.strapiService.homeVideoUrl) {
            this.videoSrc = this.strapiService.homeVideoUrl;
            this.homepageVideoUrl = this.videoSrc;
        } else {
            // Fallback: load video if not cached (first load)
            await this.loadHomepageVideo();
        }

        if (this.videoSrc && this.bgVideo?.nativeElement) {
            this.scheduleHeroVideoStart();
        }

        // Continue loading other data in the background (don't block video)
        // loadCountryFlag depends on homeVideoMain being loaded first, so chain it
        Promise.all([
            this.loadLicenseSection(),
            this.loadHomeVideoMain().then(() => this.loadCountryFlag()),
            this.loadVehicleModels(),
            this.loadHeritageData(),
            this.loadFooterVehicles(),
        ]).then(() => {
        }).catch(error => {
            console.error('Error loading landing data:', error);
        });
    }

    // Normalize an absolute Strapi media URL to the /cms-media/ proxy path.
    // Ensures SSR-baked src and client-resolved src are identical, preventing
    // the browser from issuing a second video fetch after hydration.
    private normalizeToMediaProxy(url: string): string {
        if (!url || url.startsWith('/')) return url;
        const base = this.strapiService.strapiMediaUrl;
        if (base && url.startsWith(base)) {
            return '/cms-media' + url.slice(base.length);
        }
        return url;
    }

    private applyResolvedLandingPageData(pageData: any): void {
        const homeVideo = pageData?.homeVideo;
        if (this.isLocal && homeVideo?.localVideoPath) {
            this.videoSrc = homeVideo.localVideoPath;
        } else if (homeVideo?.video?.url) {
            this.videoSrc = this.normalizeToMediaProxy(
                this.strapiService.getFullImageUrl(homeVideo.video.url) || ''
            );
        }

        this.homepageVideoUrl = this.videoSrc;

        // Note: videoElementSrc is NOT set here. On initial load it is set in ngOnInit
        // (the only call site where eager src binding is safe). On SPA navigation,
        // startHeroVideo() sets it after video.muted=true to prevent premature autoplay
        // with sound when the browser audio context is unlocked from a user click.

        if (this.videoSrc && this.bgVideo?.nativeElement) {
            this.scheduleHeroVideoStart();
        }

        this.licenseSection = {
            ...(pageData?.homeVideoLicenseSection || {}),
            images: (pageData?.homeVideoLicenseSection?.images || []).map((img: any) => ({
                ...img,
                fullUrl: img.fullUrl || this.strapiService.getFullImageUrl(img.url)
            }))
        };

        this.homeVideoMain = {
            ...(pageData?.homeVideoMain || {}),
            titleImageUrl: pageData?.homeVideoMain?.titleImageUrl || this.strapiService.getFullImageUrl(pageData?.homeVideoMain?.titleImage?.url),
            countryFlagImageUrl: pageData?.homeVideoMain?.countryFlagImageUrl || this.strapiService.getFullImageUrl(pageData?.homeVideoMain?.countryFlagImage?.url),
            galleryImages: (pageData?.homeVideoMain?.galleryImages || []).map((img: any) => ({
                ...img,
                fullUrl: img.fullUrl || this.strapiService.getFullImageUrl(img.url)
            }))
        };

        this.countryFlagImage = this.homeVideoMain?.countryFlagImage || null;
        this.heritageData = pageData?.heritage || null;
        this.heritageFacts = this.heritageData?.facts || [];
        this.vehiclesService.setVehicleModels(pageData?.vehicles || []);
        this.vehicles = pageData?.footerVehicles || [];
        this.isLoadingVehicles = false;
        this.cdr.detectChanges();
    }

    async loadFooterVehicles(): Promise<void> {
        try {
            this.isLoadingVehicles = true;
            // Use Transfer State to prevent duplicate API calls on SSR hydration
            const res: any = await lastValueFrom(
                this._transferState.useTransferState(
                    'footer-vehicles',
                    () => this.strapiService.getVehicleModels()
                )
            );
            this.vehicles = res.data?.map((vehicle: any) => ({
                id: vehicle.id,
                documentId: vehicle.documentId,
                name: vehicle.name,
                hasSubVehicles: vehicle.hasSubVehicles || false
            })) || [];
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading vehicles:', error);
            this.vehicles = [];
        } finally {
            this.isLoadingVehicles = false;
        }
    }

    async loadCountryFlag(): Promise<void> {
        try {
            // Use already-loaded homeVideoMain data to avoid duplicate API call
            // homeVideoMain is loaded in loadHomeVideoMain() above
            const flagData = this.homeVideoMain?.countryFlagImage;

            if (flagData) {
                // Flag image URL should already be processed in loadHomeVideoMain
                this.countryFlagImage = flagData;
            } else {
                this.countryFlagImage = null;
            }
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Error loading country flag:', error);
            this.countryFlagImage = null;
        }
    }

    navigateToVehicle(vehicle: any): void {
        if (vehicle.hasSubVehicles) {
            this._router.navigate(['/vehicles', slugify(vehicle.name)], { state: { id: vehicle.id } });
        } else {
            this._router.navigate(['/vehicles', slugify(vehicle.name), 'detail'], { state: { id: vehicle.id } });
        }
    }

    navigateToAbout(): void {
        this._router.navigate(['/about']);
    }

    navigateToGallery(): void {
        this._router.navigate(['/gallery']);
    }

    navigateToFAQ(): void {
        this._router.navigate(['/faq']);
    }

    navigateToPrivacyPolicy(): void {
        this._router.navigate(['/privacy-policy']);
    }

    navigateToContact(): void {
        this._router.navigate(['/contact']);
    }

    navigateToLicense(): void {
        this._router.navigate(['/license']);
    }

    onHeritageCardClick(fact: HeritageFact): void {
        if (fact.hyperlink) {
            // Check if it's a YouTube video
            if (this.isYouTubeUrl(fact.hyperlink)) {
                this.openVideoModal(fact.hyperlink);
            }
            // Check if it's an internal route (starts with /)
            else if (fact.hyperlink.startsWith('/')) {
                this._router.navigate([fact.hyperlink]);
            } else {
                // External link - open in new tab
                window.open(fact.hyperlink, '_blank');
            }
        }
    }

    // Helper method to check if URL is a YouTube URL
    private isYouTubeUrl(url: string): boolean {
        return url.includes('youtube.com') || url.includes('youtu.be');
    }

    // Helper method to convert YouTube watch URLs to embed URLs
    private getYouTubeEmbedUrl(url: string): SafeResourceUrl {
        if (!url) return this._domSanitizer.bypassSecurityTrustResourceUrl('');

        // Handle different YouTube URL formats
        const regexes = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /youtube\.com\/watch\?.*v=([^&\n?#]+)/
        ];

        let embedUrl = url;
        for (const regex of regexes) {
            const match = url.match(regex);
            if (match && match[1]) {
                embedUrl = `https://www.youtube.com/embed/${match[1]}?autoplay=1`;
                break;
            }
        }

        // Return sanitized URL to prevent XSS issues
        return this._domSanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    }

    // Open video modal for heritage card YouTube videos
    private openVideoModal(videoUrl: string): void {
        this.selectedVideoUrl = this.getYouTubeEmbedUrl(videoUrl);
        this.cdr.detectChanges();
    }

    // Close video modal
    closeVideoModal(): void {
        this.selectedVideoUrl = null;
    }
}
